class PermissionService {
    constructor() {
        this.user = null;
        this.permissions = [];
        this.initialize();
    }

    initialize() {
        try {
            const userData = localStorage.getItem('user');
            if (userData) {
                this.user = JSON.parse(userData);
                this.permissions = this.user?.permissions || [];
            }
        } catch (error) {
            console.error('Error initializing PermissionService:', error);
            this.user = null;
            this.permissions = [];
        }
    }

    // Check if user has specific permission
    hasPermission(permissionName, action = 'view') {
        if (!this.permissions || this.permissions.length === 0) {
            return false;
        }

        const permission = this.permissions.find(p =>
            p.name.toLowerCase() === permissionName.toLowerCase()
        );

        if (!permission) {
            return false;
        }

        // Map action to permission property
        const actionMap = {
            'view': 'view',
            'read': 'read',
            'add': 'add',
            'create': 'add',
            'edit': 'edit',
            'update': 'edit',
            'delete': 'delete',
            'remove': 'delete'
        };

        const permissionAction = actionMap[action] || action;
        return permission[permissionAction] === true;
    }

    // Check if user has any of the given permissions
    hasAnyPermission(permissionActions) {
        return permissionActions.some(({ permission, action = 'view' }) =>
            this.hasPermission(permission, action)
        );
    }

    // Check if user has all of the given permissions
    hasAllPermissions(permissionActions) {
        return permissionActions.every(({ permission, action = 'view' }) =>
            this.hasPermission(permission, action)
        );
    }

    // Get user role
    getUserRole() {
        return this.user?.role || 'user';
    }

    // Check if user has specific role
    hasRole(role) {
        return this.getUserRole() === role;
    }

    // Check if user is admin
    isAdmin() {
        return this.getUserRole() === 'admin';
    }

    // Check if user is manager
    isManager() {
        return this.getUserRole() === 'manager';
    }

    // Get all permissions for debugging
    getAllPermissions() {
        return this.permissions;
    }

    // Refresh permissions from localStorage
    refreshPermissions() {
        this.initialize();
    }

    // Check if user can access a screen/component
    canAccessScreen(screenName, requiredAction = 'view') {
        return this.hasPermission(screenName, requiredAction);
    }

    // Get accessible screens based on permissions
    getAccessibleScreens() {
        if (!this.permissions || this.permissions.length === 0) {
            return [];
        }

        return this.permissions
            .filter(perm => perm.view === true)
            .map(perm => perm.name);
    }

    // Check if user can perform action on a module
    canPerformAction(moduleName, action) {
        return this.hasPermission(moduleName, action);
    }

    // Get user branches
    getUserBranches() {
        return this.user?.branches || [];
    }

    // Check if user has access to specific branch
    hasBranchAccess(branchId) {
        if (this.isAdmin()) {
            return true; // Admin has access to all branches
        }

        const userBranches = this.getUserBranches();
        return userBranches.some(branch => branch.branch_id === branchId);
    }
}

// Create singleton instance
const permissionService = new PermissionService();

export default permissionService;